import { COVER_WIDTH } from "@/lib/process-cover-image"

export const MAX_COVER_VIDEO_MB = 100

export const COVER_FILE_ACCEPT = "image/*,video/mp4,video/webm,video/quicktime"

interface CoverRow {
	imageUrl?: string | null
	coverVideoUrl?: string | null
}

export const isVideoFile = (file: Pick<File, "type">) =>
	file.type.startsWith("video/")

// The cover is one pair: a translation with its own image owns its video slot
// too (null means an image cover), so a base-row video never plays over a
// translated image.
export const coverFor = (translation: CoverRow, changelog: CoverRow) =>
	translation.imageUrl
		? {
				imageUrl: translation.imageUrl,
				coverVideoUrl: translation.coverVideoUrl ?? null,
			}
		: {
				imageUrl: changelog.imageUrl ?? null,
				coverVideoUrl: changelog.coverVideoUrl ?? null,
			}

// The first frame of a screen recording is often a blank fade-in; a second in
// shows the product, and a short clip falls back to its own middle.
export const posterTimeFor = (durationSeconds: number) => {
	if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0
	return Math.min(1, durationSeconds / 2)
}

export const posterSizeFor = (width: number, height: number) => {
	if (width <= COVER_WIDTH) return { width, height }
	return {
		width: COVER_WIDTH,
		height: Math.round((height * COVER_WIDTH) / width),
	}
}

const once = (target: HTMLVideoElement, event: string) =>
	new Promise<void>((resolve, reject) => {
		target.addEventListener(event, () => resolve(), { once: true })
		target.addEventListener(
			"error",
			() => reject(new Error("This video can't be read")),
			{ once: true },
		)
	})

/** A JPEG still of the video, used as its poster, thumbnail and og:image. */
export async function captureVideoPoster(file: File): Promise<File> {
	const url = URL.createObjectURL(file)
	const video = document.createElement("video")
	video.muted = true
	video.playsInline = true
	video.preload = "auto"
	video.src = url
	try {
		await once(video, "loadedmetadata")
		const seeked = once(video, "seeked")
		video.currentTime = posterTimeFor(video.duration)
		await seeked

		const { width, height } = posterSizeFor(video.videoWidth, video.videoHeight)
		const canvas = document.createElement("canvas")
		canvas.width = width
		canvas.height = height
		const context = canvas.getContext("2d")
		if (!context) throw new Error("Canvas is not available")
		context.drawImage(video, 0, 0, width, height)

		const blob = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, "image/jpeg", 0.9),
		)
		if (!blob) throw new Error("Couldn't capture a frame from this video")
		const base = file.name.replace(/\.[^.]+$/, "") || "cover"
		return new File([blob], `${base}-poster.jpg`, { type: "image/jpeg" })
	} finally {
		URL.revokeObjectURL(url)
		video.removeAttribute("src")
		video.load()
	}
}
